import socialApiClient from "../../helpers/services/social-api-client";
import axios from "axios";
import { ContentGridRow } from "../../../ui/utils/socialContentGridShare";

export type ContentGridSummary = {
  id: number;
  companyId: number;
  name: string;
  month: string;
  status: string;
  updatedAt: string;
  createdAt: string;
};

export type ContentGridPayload = {
  id: number;
  companyId: number;
  name: string;
  month: string;
  status: string;
  rows: ContentGridRow[];
  createdAt?: string;
  updatedAt?: string;
};

const client = () => socialApiClient();

export const contentGridApi = {
  list: async (companyId: number) => {
    const { data } = await client().get<{ payload: ContentGridSummary[] }>(
      "/social/content-grid",
      { params: { companyId } }
    );
    return data.payload;
  },

  getDefault: async (companyId: number) => {
    const { data } = await client().get<{ payload: ContentGridPayload }>(
      "/social/content-grid/default",
      { params: { companyId } }
    );
    return data.payload;
  },

  get: async (gridId: number, companyId: number) => {
    const { data } = await client().get<{ payload: ContentGridPayload }>(
      `/social/content-grid/${gridId}`,
      { params: { companyId } }
    );
    return data.payload;
  },

  create: async (body: {
    companyId: number;
    name?: string;
    month?: string;
    duplicateFromId?: number;
  }) => {
    const { data } = await client().post<{ payload: ContentGridPayload }>(
      "/social/content-grid",
      body,
      { params: { companyId: body.companyId } }
    );
    return data.payload;
  },

  updateMeta: async (
    gridId: number,
    body: { companyId: number; name?: string; month?: string; status?: string }
  ) => {
    const { data } = await client().patch<{ payload: ContentGridPayload }>(
      `/social/content-grid/${gridId}`,
      body,
      { params: { companyId: body.companyId } }
    );
    return data.payload;
  },

  saveRows: async (gridId: number, companyId: number, rows: ContentGridRow[]) => {
    const { data } = await client().put<{ payload: ContentGridPayload }>(
      `/social/content-grid/${gridId}/rows`,
      { companyId, rows },
      { params: { companyId } }
    );
    return data.payload;
  },

  addRowsBatch: async (
    gridId: number,
    companyId: number,
    rows: ContentGridRow[]
  ) => {
    const { data } = await client().post<{ payload: ContentGridPayload }>(
      `/social/content-grid/${gridId}/rows/batch`,
      { companyId, rows },
      { params: { companyId } }
    );
    return data.payload;
  },

  uploadMedia: async (
    gridId: number,
    rowId: string,
    companyId: number,
    file: File,
    mediaType: "image" | "video"
  ) => {
    const form = new FormData();
    form.append("file", file);
    form.append("companyId", String(companyId));
    form.append("mediaType", mediaType);
    const { data } = await client().post<{ payload: ContentGridRow }>(
      `/social/content-grid/${gridId}/rows/${rowId}/media`,
      form,
      {
        headers: { "Content-Type": "multipart/form-data" },
        params: { companyId },
      }
    );
    return data.payload;
  },

  createShareLink: async (
    gridId: number,
    companyId: number,
    expiresInDays = 30
  ) => {
    const { data } = await client().post<{
      payload: { token: string; expiresAt: string | null };
    }>(
      `/social/content-grid/${gridId}/share`,
      { companyId, expiresInDays },
      { params: { companyId } }
    );
    return data.payload;
  },

  sendToCalendar: async (
    gridId: number,
    companyId: number,
    rowIds: string[],
    network?: string
  ) => {
    const { data } = await client().post<{
      payload: {
        results: Array<{
          rowId: string;
          socialPostId?: number;
          error?: string;
          skipped?: boolean;
        }>;
        grid: ContentGridPayload;
      };
    }>(
      `/social/content-grid/${gridId}/send-to-calendar`,
      {
        companyId,
        rowIds,
        network,
      },
      { params: { companyId } }
    );
    return data.payload;
  },

  duplicateRow: async (gridId: number, rowId: string, companyId: number) => {
    const { data } = await client().post<{ payload: ContentGridPayload }>(
      `/social/content-grid/${gridId}/rows/${rowId}/duplicate`,
      { companyId },
      { params: { companyId } }
    );
    return data.payload;
  },

  migrateFromClient: async (
    companyId: number,
    rows: ContentGridRow[],
    gridName?: string
  ) => {
    const { data } = await client().post<{ payload: ContentGridPayload }>(
      "/social/content-grid/migrate",
      { companyId, rows, gridName },
      { params: { companyId } }
    );
    return data.payload;
  },
};

const publicBaseUrl =
  process.env.REACT_APP_SOCIAL_URL || "http://localhost:5001/api";

export const contentGridPublicApi = {
  getByToken: async (token: string) => {
    const { data } = await axios.get<{ payload: ContentGridPayload }>(
      `${publicBaseUrl}/public/content-grid/${token}`
    );
    return data.payload;
  },

  decide: async (
    token: string,
    rowId: string,
    body: { decision: "approved" | "rejected"; comments?: string }
  ) => {
    const { data } = await axios.patch<{ payload: ContentGridPayload }>(
      `${publicBaseUrl}/public/content-grid/${token}/rows/${rowId}/decision`,
      body
    );
    return data.payload;
  },

  updateComment: async (token: string, rowId: string, comments: string) => {
    const { data } = await axios.patch<{ payload: ContentGridPayload }>(
      `${publicBaseUrl}/public/content-grid/${token}/rows/${rowId}/comments`,
      { comments }
    );
    return data.payload;
  },
};

export const buildPublicContentGridUrl = (token: string) =>
  `${window.location.origin}/public/content-grid/${token}`;
